import type {
  CartLine,
  Product,
  ProductVariant,
  ProductVariantConfig,
  ProductVariantConfigOption,
} from '../types/models';

export function parseProductVariantConfig(raw: unknown): ProductVariantConfig | undefined {
  if (!raw || typeof raw !== 'object') return undefined;
  const o = raw as Record<string, unknown>;
  const optionsRaw = o.options;
  if (!Array.isArray(optionsRaw) || optionsRaw.length === 0) return undefined;

  const options: ProductVariantConfigOption[] = [];
  for (const item of optionsRaw) {
    if (!item || typeof item !== 'object') continue;
    const row = item as Record<string, unknown>;
    const id = String(row.id ?? '').trim();
    const label = String(row.label ?? '').trim();
    if (!id || !label) continue;
    options.push({
      id,
      label,
      emoji: row.emoji ? String(row.emoji) : undefined,
      inStock: row.inStock !== false,
    });
  }

  if (options.length === 0) return undefined;

  const minSelections = Math.max(0, Math.floor(Number(o.minSelections) || 0));
  const maxSelections = Math.max(minSelections, Math.floor(Number(o.maxSelections) || options.length));
  const label = String(o.label ?? 'Choose your options').trim() || 'Choose your options';

  return {
    label,
    minSelections,
    maxSelections,
    seedlingsPerVariety:
      o.seedlingsPerVariety != null ? Math.max(0, Math.floor(Number(o.seedlingsPerVariety))) : undefined,
    options,
  };
}

/** Build picker config from seller variant rows (vendor form sync). */
export function buildVariantConfigFromVariants(
  variants: ProductVariant[],
  partial?: Partial<ProductVariantConfig>,
): ProductVariantConfig | undefined {
  if (variants.length === 0) return undefined;
  const options: ProductVariantConfigOption[] = variants.map((v) => ({
    id: v.id,
    label: v.label.trim() || v.id,
    emoji: v.attributes?.emoji?.trim() || undefined,
    inStock: v.stock > 0,
  }));
  const max = Math.max(1, partial?.maxSelections ?? options.length);
  const min = Math.min(max, Math.max(1, partial?.minSelections ?? max));
  return {
    label: partial?.label?.trim() || 'Choose your options',
    minSelections: min,
    maxSelections: max,
    seedlingsPerVariety: partial?.seedlingsPerVariety,
    options,
  };
}

export function productVariantConfig(product: Product): ProductVariantConfig | undefined {
  if (product.variantConfig?.options?.length) return product.variantConfig;
  return undefined;
}

export function formatVariantPickerSummary(
  config: ProductVariantConfig,
  selectedIds: string[],
): string {
  const labels = selectedIds
    .map((id) => config.options.find((o) => o.id === id)?.label)
    .filter((l): l is string => Boolean(l));
  return labels.join(', ');
}

export function variantPickerSelectedCount(selectedIds: string[]): number {
  return selectedIds.length;
}

export function variantPickerSelectionComplete(
  config: ProductVariantConfig,
  selectedCount: number,
): boolean {
  return selectedCount >= config.minSelections && selectedCount <= config.maxSelections;
}

export function variantPickerCanSelectMore(
  config: ProductVariantConfig,
  selectedCount: number,
): boolean {
  return selectedCount < config.maxSelections;
}

export function variantPickerAddToCartLabel(
  config: ProductVariantConfig,
  selectedCount: number,
  priceCents: number,
  currency: string,
): string {
  const required = config.maxSelections;
  const remaining = required - selectedCount;
  const formatted = new Intl.NumberFormat('en-GD', {
    style: 'currency',
    currency,
    maximumFractionDigits: priceCents % 100 === 0 ? 0 : 2,
  }).format(priceCents / 100);

  if (selectedCount === 0) {
    return `Select ${required} ${required === 1 ? 'option' : 'options'} to continue`;
  }
  if (remaining > 0) {
    const noun = remaining === 1 ? 'option' : 'options';
    return `Select ${remaining} more ${noun}`;
  }
  return `Add to cart — ${formatted}`;
}

export function cartLineIdentity(line: Pick<CartLine, 'productId' | 'variantId' | 'selectedVariantIds'>): string {
  if (line.selectedVariantIds?.length) {
    return `${line.productId}:${[...line.selectedVariantIds].sort().join(',')}`;
  }
  return `${line.productId}:${line.variantId ?? ''}`;
}

export function cartLineVariantKey(line: CartLine): string | undefined {
  if (line.selectedVariantIds?.length) {
    return [...line.selectedVariantIds].sort().join(',');
  }
  return line.variantId;
}

export function buildConfigurableCartLine(input: {
  product: Product;
  quantity: number;
  selectedIds: string[];
  config: ProductVariantConfig;
}): CartLine {
  const sorted = [...input.selectedIds].sort();
  const summary = formatVariantPickerSummary(input.config, sorted);
  return {
    productId: input.product.id,
    quantity: input.quantity,
    title: input.product.title,
    priceCents: input.product.priceCents,
    image: input.product.images[0],
    vendorId: input.product.vendorId,
    variantId: sorted.join(','),
    variantLabel: summary,
    selectedVariantIds: sorted,
    variantSummary: summary,
  };
}
